import type { LlmAnalysisBreakdownBucket } from '../data-access/project.api';
import { formatLogLabel } from './formatters';

const BREAKDOWN_SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Unknown', 'Unspecified'] as const;

export type LlmBreakdownStackedChartData = {
  analysisTypes: Array<{ key: string; label: string }>;
  segments: Array<{
    severity: string;
    counts: number[];
    avgScores: (number | null)[];
  }>;
};

export function normalizeBreakdownSeverity(severity: string | null): string {
  const normalized = severity?.trim();
  if (!normalized) return 'Unspecified';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

/** Group API `breakdown` for a stacked bar per analysis type (segments = severity). */
export function mapLlmBreakdownToStackedChart(
  breakdown: Record<string, LlmAnalysisBreakdownBucket[]> | undefined
): LlmBreakdownStackedChartData | null {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const typeEntries = Object.entries(breakdown).map(([key, buckets]) => ({
    key,
    label: formatLogLabel(key),
    total: buckets.reduce((sum, bucket) => sum + bucket.count, 0),
    buckets
  }));
  typeEntries.sort((a, b) => b.total - a.total);

  const analysisTypes = typeEntries.map(({ key, label }) => ({ key, label }));

  const severityKeys = new Set<string>();
  for (const entry of typeEntries) {
    for (const bucket of entry.buckets) {
      severityKeys.add(normalizeBreakdownSeverity(bucket.severity));
    }
  }

  const orderedSeverities: string[] = [
    ...BREAKDOWN_SEVERITY_ORDER.filter(severity => severityKeys.has(severity)),
    ...[...severityKeys].filter(
      severity => !BREAKDOWN_SEVERITY_ORDER.includes(severity as (typeof BREAKDOWN_SEVERITY_ORDER)[number])
    )
  ];

  const segments = orderedSeverities
    .map(severity => {
      const counts: number[] = [];
      const avgScores: (number | null)[] = [];
      for (const entry of typeEntries) {
        const bucket = entry.buckets.find(b => normalizeBreakdownSeverity(b.severity) === severity);
        counts.push(bucket?.count ?? 0);
        avgScores.push(bucket?.avg_score ?? null);
      }
      return { severity, counts, avgScores };
    })
    .filter(segment => segment.counts.some(count => count > 0));

  if (!segments.length) return null;

  return { analysisTypes, segments };
}
